import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface ActionItem {
  title: string;
  description: string;
  route: string;
}

export default function ComptableDashboardPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [comptableInfo, setComptableInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPatients: 0,
    consultationsAujourdhui: 0,
    analysesEnCours: 0,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'comptable') { navigate('/', { replace: true }); return; }
    loadComptableInfo();
  }, [user, role, authLoading]);

  const loadComptableInfo = async () => {
    const { data } = await supabase.from('comptables').select('*').eq('user_id', user!.id).maybeSingle();
    if (data) {
      setComptableInfo(data);
      loadStats();
    } else {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [patientsRes, consultationsRes, analysesRes] = await Promise.all([
        supabase.from('patients').select('id', { count: 'exact', head: true }),
        supabase.from('consultations').select('id', { count: 'exact', head: true }).gte('date_consultation', today),
        supabase.from('dossiers_analyses').select('id', { count: 'exact', head: true }).eq('statut', 'en_cours'),
      ]);
      setStats({
        totalPatients: patientsRes.count || 0,
        consultationsAujourdhui: consultationsRes.count || 0,
        analysesEnCours: analysesRes.count || 0,
      });
    } catch (e) {
      // Silencieux
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
          <p className="text-clinic-blue-dark text-xl">Chargement...</p>
        </div>
      </div>
    );
  }

  const actionItems: ActionItem[] = [
    { title: 'Consultations', description: 'Consulter l\'historique des consultations', route: '/admin/consultations' },
    { title: 'Rendez-vous', description: 'Consulter les rendez-vous de la clinique', route: '/admin/rendez-vous' },
    { title: 'Patients', description: 'Superviser les dossiers patients', route: '/admin/patients' },
    { title: 'Statistiques', description: 'Analyses et rapports d\'activité', route: '/admin/statistiques' },
  ];

  const statItems = [
    { label: 'Patients', value: stats.totalPatients },
    { label: 'Consultations aujourd\'hui', value: stats.consultationsAujourdhui },
    { label: 'Analyses en cours', value: stats.analysesEnCours },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFB]">
      <Header />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
        {/* Titre */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Tableau de bord Comptable</h1>
            <span className="rounded-full border border-clinic-blue/20 bg-clinic-blue/5 px-3 py-1 text-xs font-semibold text-clinic-blue-dark whitespace-nowrap">
              {comptableInfo?.prenom} {comptableInfo?.nom}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">Suivi des indicateurs financiers et statistiques de la clinique</p>
        </div>

        {/* Stats */}
        <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {statItems.map((stat) => (
            <div key={stat.label} className="rounded-xl border border-gray-100 bg-white px-5 py-4">
              <p className="text-2xl font-bold text-clinic-blue-dark md:text-3xl">{stat.value}</p>
              <p className="mt-1 text-xs text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Accès rapides */}
        <div className="mb-4 flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-900">Accès rapides</h2>
          <span className="h-px flex-1 bg-gray-100"></span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {actionItems.map((item) => (
            <Link
              key={item.title}
              to={item.route}
              className="group flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-white px-5 py-4 transition-all duration-200 hover:border-clinic-blue/30 hover:bg-clinic-blue/[0.03]"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 group-hover:text-clinic-blue-dark transition-colors">
                  {item.title}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">{item.description}</p>
              </div>
              <i className="ri-arrow-right-s-line text-lg text-gray-300 group-hover:text-clinic-blue group-hover:translate-x-0.5 transition-all flex-shrink-0"></i>
            </Link>
          ))}
        </div>

        {/* Note */}
        <div className="mt-8 rounded-xl border border-gray-100 bg-white px-5 py-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Les fonctionnalités de gestion comptable détaillées seront disponibles prochainement.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}